import { Component, DestroyRef, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { faArrowDown, faArrowUp, faSearch, faSort } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, first, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { indicate } from 'src/app/operators';
import { MultiSelectFilterComponent } from 'src/app/search/filters/multi-select/multi-select-filter/multi-select-filter.component';
import {
  EsraPipingAPIClient,
  PipeClassViewModel,
  PipingRevisionViewModel,
  RevisionStatus,
  UserType,
  ValveViewModel
} from 'src/app/shared/models/autogenerated-piping';

class ReviewFilter {
  filterName: string | null;
  options: string[];

  constructor(filterName: string, options: string[]) {
    this.filterName = filterName;
    this.options = options;
  }
}

class ReviewPortalTableObject {
  revisionId: number;
  title?: string;
  releaseName?: string;
  componentId?: string;
  authorName?: string;
  // Array of display names of all reviewers on the revision — stored as array to avoid
  // ambiguous splitting when names contain commas (e.g. "LastName, FirstName" format)
  reviewerNames?: string[];
  status?: RevisionStatus;
  bu?: string;
  application?: string;
  component?: string;
  editRoute?: string;
}

@Component({
  selector: 'app-my-review-portal',
  templateUrl: './my-review-portal.component.html',
  styleUrls: ['./my-review-portal.component.scss'],
  standalone: false
})
export class MyReviewPortalComponent implements OnInit {
  private readonly esraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  faSearch = faSearch;
  faSort = faSort;
  faArrowDown = faArrowDown;
  faArrowUp = faArrowUp;

  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // MatTableDataSource holds the current page slice only
  dataSource: MatTableDataSource<ReviewPortalTableObject> = new MatTableDataSource<ReviewPortalTableObject>([]);

  columnNames: string[] = ['title', 'releaseName', 'author', 'reviewer', 'status', 'bu', 'application', 'component'];

  // ── Filters ────────────────────────────────────────────────────────────
  filters: ReviewFilter[] = [];
  private filterSelections: Map<string, Set<string>> = new Map<string, Set<string>>();
  searchText: string = '';

  private titles: string[] = [];
  private releaseNames: string[] = [];
  private authors: string[] = [];
  private reviewers: string[] = [];
  private statuses: number[] = [];
  private businessUnits: string[] = [];
  private applications: string[] = [];
  private components: string[] = [];

  // ── Data ───────────────────────────────────────────────────────────────
  private allTableObjects: ReviewPortalTableObject[] = [];
  private filteredTableObjects: ReviewPortalTableObject[] = [];

  // ── Sort ───────────────────────────────────────────────────────────────
  activeSortColumn: string = '';
  sortAscending: boolean = false;

  // ── Pagination ─────────────────────────────────────────────────────────
  paginatorTotalCount: number = 0;
  currentPage: number = 0;
  pageSize: number = 10;
  pageSizes: number[] = [5, 10, 25, 50, 100];

  @ViewChildren(MultiSelectFilterComponent) childFilters: QueryList<MultiSelectFilterComponent>;

  loadError: boolean = false;

  ngOnInit(): void {
    this.esraPipingAPIClient.getMyWorkspace2()
      .pipe(
        indicate(this.loading$),
        first(),
        catchError(() => {
          this.loadError = true;
          return of([]);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(revisions => {
        this.allTableObjects = this.buildTableObjects(revisions);
        this.initializeFilterOptions(this.allTableObjects);
        this.applyAllFilters();
      });
  }

  private buildTableObjects(revisions: PipingRevisionViewModel[]): ReviewPortalTableObject[] {
    const result: ReviewPortalTableObject[] = [];

    revisions.forEach(revision => {
      // Exclude DRAFT revisions — the workspace only shows revisions pending review

      if (revision.status === RevisionStatus.DRAFT) {
        return;
      }

      //Exclude Approved revisions -  the workspace will not display revisions already approved
      if (revision.status === RevisionStatus.APPROVAL) {
        return;
      }

      // Collect reviewer display names from the roles array (internal + external reviewers).
      // Stored as an array so names containing commas (LastName, FirstName) are never split incorrectly.
      const reviewerNames: string[] = (revision.roles ?? [])
        .filter(role => role.userRole === UserType._2 || role.userRole === UserType._3)
        .map(role => role.userDisplayName ?? role.userEmail ?? '')
        .filter(name => name !== '');

      if (revision.pipeClasses != null) {
        revision.pipeClasses.forEach((pipeClass: PipeClassViewModel) => {
          result.push({
            revisionId: revision.id,
            title: pipeClass.name,
            releaseName: revision.name,
            componentId: pipeClass.id,
            authorName: revision.authorName,
            reviewerNames: reviewerNames,
            status: revision.status,
            bu: pipeClass.businessUnits?.[0] ?? '',
            application: pipeClass.application,
            component: 'pipe',
            editRoute: 'pipingsummary/'
          });
        });
      }

      if (revision.valves != null) {
        revision.valves.forEach((valve: ValveViewModel) => {
          result.push({
            revisionId: revision.id,
            title: valve.tag?.split('_')[0],
            releaseName: revision.name,
            componentId: valve.id,
            authorName: revision.authorName,
            reviewerNames: reviewerNames,
            status: revision.status,
            bu: valve.businessUnits?.[0] ?? '',
            application: valve.application ?? '',
            component: 'valve',
            editRoute: 'valvesummary/'
          });
        });
      }
    });

    return result;
  }

  initializeFilterOptions(tableObjects: ReviewPortalTableObject[]): void {
    tableObjects.forEach(obj => {
      if (obj.title && !this.titles.includes(obj.title)) {
        this.titles.push(obj.title);
      }
      if (obj.releaseName && !this.releaseNames.includes(obj.releaseName)) {
        this.releaseNames.push(obj.releaseName);
      }
      if (obj.authorName && !this.authors.includes(obj.authorName)) {
        this.authors.push(obj.authorName);
      }
      // Each reviewerNames entry is a complete name — no splitting needed
      if (obj.reviewerNames) {
        obj.reviewerNames.forEach(name => {
          if (name && !this.reviewers.includes(name)) {
            this.reviewers.push(name);
          }
        });
      }
      if (obj.status != null && !this.statuses.includes(obj.status)) {
        this.statuses.push(obj.status);
      }
      if (obj.bu && !this.businessUnits.includes(obj.bu)) {
        this.businessUnits.push(obj.bu);
      }
      if (obj.application && !this.applications.includes(obj.application)) {
        this.applications.push(obj.application);
      }
      if (obj.component && !this.components.includes(obj.component)) {
        this.components.push(obj.component);
      }
    });

    const uniqueStatusLabels: string[] = this.statuses.map(s => this.convertStatusToString(s));

    if (this.titles.length > 0) {
      this.filters.push(new ReviewFilter('Title', this.titles));
    }
    if (this.releaseNames.length > 0) {
      this.filters.push(new ReviewFilter('Release name', this.releaseNames));
    }
    if (this.authors.length > 0) {
      this.filters.push(new ReviewFilter('Author', this.authors));
    }
    if (this.reviewers.length > 0) {
      this.filters.push(new ReviewFilter('Reviewer', this.reviewers));
    }
    if (this.statuses.length > 0) {
      this.filters.push(new ReviewFilter('Status', uniqueStatusLabels));
    }
    if (this.businessUnits.length > 0) {
      this.filters.push(new ReviewFilter('BU', this.businessUnits));
    }
    if (this.applications.length > 0) {
      this.filters.push(new ReviewFilter('Application', this.applications));
    }
    if (this.components.length > 0) {
      this.filters.push(new ReviewFilter('Component', this.components));
    }
  }

  onCheckboxChange(selectedValues: Set<string>, filter: ReviewFilter): void {
    const filterName: string = filter.filterName ?? '';
    if (selectedValues.size === 0) {
      this.filterSelections.delete(filterName);
    } else {
      this.filterSelections.set(filterName, new Set<string>(selectedValues));
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  clearAllFilters(): void {
    this.childFilters.forEach(childFilter => childFilter.clearSelections());
    this.filterSelections.clear();
    this.searchText = '';
    this.currentPage = 0;
    this.applyAllFilters();
  }

  callSearch(searchQuery: string): void {
    this.searchText = searchQuery;
    this.currentPage = 0;
    this.applyAllFilters();
  }

  private applyAllFilters(): void {
    let result: ReviewPortalTableObject[] = [...this.allTableObjects];

    for (const [filterName, selectedValues] of this.filterSelections) {
      if (selectedValues.size === 0) { continue; }
      result = result.filter(obj => {
        switch (filterName) {
          case 'Title':        return selectedValues.has(obj.title ?? '');
          case 'Release name': return selectedValues.has(obj.releaseName ?? '');
          case 'Author':       return selectedValues.has(obj.authorName ?? '');
          case 'Reviewer':     return [...selectedValues].some(r => (obj.reviewerNames ?? []).includes(r));
          case 'Status':       return selectedValues.has(this.convertStatusToString(obj.status ?? 0));
          case 'BU':           return selectedValues.has(obj.bu ?? '');
          case 'Application':  return selectedValues.has(obj.application ?? '');
          case 'Component':    return selectedValues.has(obj.component ?? '');
          default:             return true;
        }
      });
    }

    if (this.searchText) {
      const term: string = this.searchText.toLowerCase().trim();
      result = result.filter(obj =>
        this.lowerCaseAndTrim(obj.title ?? '').includes(term)
        || this.lowerCaseAndTrim(obj.releaseName ?? '').includes(term)
        || this.lowerCaseAndTrim(obj.authorName ?? '').includes(term)
        || (obj.reviewerNames ?? []).some(name => this.lowerCaseAndTrim(name).includes(term))
        || this.lowerCaseAndTrim(this.convertStatusToString(obj.status ?? 0)).includes(term)
      );
    }

    this.filteredTableObjects = result;
    this.paginatorTotalCount = result.length;
    this.applySortToResult(this.filteredTableObjects);
    this.setPagedData();
  }

  private applySortToResult(result: ReviewPortalTableObject[]): void {
    if (!this.activeSortColumn) { return; }
    const asc: boolean = this.sortAscending;
    const cmp = (a: string, b: string): number =>
      (a.toLowerCase() < b.toLowerCase() ? -1 : a.toLowerCase() > b.toLowerCase() ? 1 : 0) * (asc ? 1 : -1);

    switch (this.activeSortColumn) {
      case 'title':       result.sort((a, b) => cmp(a.title ?? '', b.title ?? '')); break;
      case 'releaseName': result.sort((a, b) => cmp(a.releaseName ?? '', b.releaseName ?? '')); break;
      case 'author':      result.sort((a, b) => cmp(a.authorName ?? '', b.authorName ?? '')); break;
      case 'reviewer':    result.sort((a, b) => cmp(a.reviewerNames?.join(', ') ?? '', b.reviewerNames?.join(', ') ?? '')); break;
      case 'status':      result.sort((a, b) => ((a.status ?? 0) - (b.status ?? 0)) * (asc ? 1 : -1)); break;
      case 'bu':          result.sort((a, b) => cmp(a.bu ?? '', b.bu ?? '')); break;
      case 'application': result.sort((a, b) => cmp(a.application ?? '', b.application ?? '')); break;
      case 'component':   result.sort((a, b) => cmp(a.component ?? '', b.component ?? '')); break;
    }
  }

  sortTable(columnName: string): void {
    if (this.activeSortColumn === columnName) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.activeSortColumn = columnName;
      this.sortAscending = true;
    }
    this.currentPage = 0;
    this.applyAllFilters();
  }

  updatePage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.setPagedData();
  }

  private setPagedData(): void {
    const start: number = this.currentPage * this.pageSize;
    const end: number = start + this.pageSize;
    this.dataSource = new MatTableDataSource<ReviewPortalTableObject>(this.filteredTableObjects.slice(start, end));
  }

  navigateToRevision(row: ReviewPortalTableObject): void {
    if (row.component === 'valve') {
      this.router.navigate(['/piping/valve/edit'], {
        queryParams: { mode: 'edit', id: row.componentId }
      });
      return;
    }
    // Pipe class: open the piping summary in edit mode.
    // Pass the navigation origin via router state so the back button in piping-tabs-parent
    // returns here. Using history.state is reliable because it is not affected by the
    // outgoing component's ngOnDestroy (unlike session storage).
    this.router.navigate([row.editRoute, row.componentId], {
      queryParams: { isEditMode: 'true' },
      state: { returnUrl: '/myworkspace' }
    });
  }

  convertStatusToString(statusNum: number): string {
    
    if (statusNum === 0) return 'Draft';
    if (statusNum === 1) return 'Under Review';
    if (statusNum === 2) return 'Ready for Publish';
    if (statusNum === 3) return 'Published';
    return '';
  }

  private lowerCaseAndTrim(input: string): string {
    return input ? input.toLowerCase().trim() : '';
  }
}
